import React from 'react';
import { motion } from 'framer-motion';
export interface PriceCompareItem {
  /** Product/offer name */
  name: string;
  /** Quantity (e.g. 3, 500) */
  quantity: number;
  /** Unit label (e.g. "stuks", "gram", "liter") */
  unit: string;
  /** Total price in euros */
  totalPrice: number;
}
export interface PriceCompareProps {
  /** Items to compare */
  items: PriceCompareItem[];
  /** What unit to compare on (e.g. "per stuk", "per kg", "per liter") */
  compareUnit?: string;
  /** Optional label above the visual */
  label?: string;
  /** 'stimulus' = compact, 'support' = educational panel */
  mode?: 'stimulus' | 'support';
}
function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}
function formatEuro(value: number): string {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}
function formatNumber(value: number): string {
  return new Intl.NumberFormat('nl-NL', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0
  }).format(value);
}
const ITEM_COLORS = [{
  bg: 'bg-blue-50',
  border: 'border-blue-200',
  accent: 'bg-blue-500',
  text: 'text-blue-800',
  light: 'text-blue-600'
}, {
  bg: 'bg-amber-50',
  border: 'border-amber-200',
  accent: 'bg-amber-500',
  text: 'text-amber-800',
  light: 'text-amber-600'
}, {
  bg: 'bg-emerald-50',
  border: 'border-emerald-200',
  accent: 'bg-emerald-500',
  text: 'text-emerald-800',
  light: 'text-emerald-600'
}, {
  bg: 'bg-purple-50',
  border: 'border-purple-200',
  accent: 'bg-purple-500',
  text: 'text-purple-800',
  light: 'text-purple-600'
}];
export function PriceCompare({
  items,
  compareUnit = 'per stuk',
  label,
  mode = 'support'
}: PriceCompareProps) {
  const validItems = (items || []).filter(item => item.name && isFiniteNumber(item.quantity) && item.quantity > 0 && isFiniteNumber(item.totalPrice) && item.totalPrice >= 0);
  if (validItems.length < 2) {
    return <div className="bg-red-50 p-4 rounded-xl border border-red-200 text-center" data-id="element-13488" data-mp="3u1omfn2veaf9">
        <p className="text-red-600 text-sm font-medium" data-id="element-13489" data-mp="2tsvwfqekgwx">
          Minimaal 2 producten nodig om te vergelijken
        </p>
      </div>;
  }
  // Calculate unit prices
  const withUnitPrice = validItems.map(item => ({
    ...item,
    unitPrice: item.totalPrice / item.quantity
  }));
  // Find cheapest
  const cheapestPrice = Math.min(...withUnitPrice.map(i => i.unitPrice));
  const cheapestIndices = withUnitPrice.map((item, i) => Math.abs(item.unitPrice - cheapestPrice) < 0.001 ? i : -1).filter(i => i >= 0);
  // Stimulus mode: compact comparison table
  if (mode === 'stimulus') {
    return <div className="bg-blue-50 rounded-xl px-5 py-4 border border-blue-200" data-id="element-13490" data-mp="2nejfdzbs0imf">
        {label && <p className="text-sm font-semibold text-blue-800 mb-3 text-center" data-id="element-13491" data-mp="2sdvxk6rpd1io">
            {label}
          </p>}
        <div className="grid gap-2" data-id="element-13492" data-mp="2g1k5x1jvdfsq">
          {withUnitPrice.map((item, i) => {
          const colors = ITEM_COLORS[i % ITEM_COLORS.length];
          return <motion.div key={i} initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: i * 0.1,
            duration: 0.3
          }} className={`flex items-center justify-between ${colors.bg} rounded-lg px-4 py-2.5 border ${colors.border}`} data-id="element-13493" data-mp="2b2qo4wxjv2mr" data-mpi={i}>
                <div className="flex items-center gap-2" data-id="element-13494" data-mp="2ptg2272eavub">
                  <div className={`w-2 h-2 rounded-full ${colors.accent}`} data-id="element-13495" data-mp="1ieutdxrjjksm" />
                  <span className={`text-sm font-semibold ${colors.text}`} data-id="element-13496" data-mp="1ebotd7v0upul">
                    {item.name}
                  </span>
                </div>
                <div className="text-right" data-id="element-13497" data-mp="327l53wz2o9s9">
                  <span className="text-xs text-slate-500" data-id="element-13498" data-mp="3704ml50vrp2c">
                    {formatNumber(item.quantity)} {item.unit} voor{' '}
                  </span>
                  <span className="text-sm font-bold text-slate-800" data-id="element-13499" data-mp="2s8nbwgqpbs2x">
                    {formatEuro(item.totalPrice)}
                  </span>
                </div>
              </motion.div>;
        })}
        </div>
      </div>;
  }
  // Support mode: full educational panel with unit price calculation
  const savings = withUnitPrice.length === 2 ? Math.abs(withUnitPrice[0].unitPrice - withUnitPrice[1].unitPrice) : null;
  return <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-2xl border-2 border-blue-200 shadow-sm" data-id="element-13500" data-mp="3fz9fnvtoaq74">
      {label && <div className="mb-5 text-center" data-id="element-13501" data-mp="20ln340iql32e">
          <div className="inline-flex items-center gap-2 bg-white/90 rounded-xl px-4 py-2 border border-blue-200 shadow-sm" data-id="element-13502" data-mp="24le40x1z8zkn">
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" data-id="element-13503" data-mp="1x01uhqyltrd5" />
            <span className="text-sm font-bold text-blue-900" data-id="element-13504" data-mp="1s3bibepx7fx0">{label}</span>
          </div>
        </div>}

      {/* Comparison cards */}
      <div className="grid gap-3 mb-5" data-id="element-13505" data-mp="26xq1bf9hipbo">
        {withUnitPrice.map((item, i) => {
        const colors = ITEM_COLORS[i % ITEM_COLORS.length];
        const isCheapest = cheapestIndices.includes(i);
        return <motion.div key={i} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.15,
          duration: 0.4
        }} className={`relative bg-white rounded-xl p-4 border-2 ${isCheapest ? 'border-emerald-400 ring-2 ring-emerald-100' : colors.border} shadow-sm`} data-id="element-13506" data-mp="19c226mln9swg" data-mpi={i}>
              {/* Cheapest badge */}
              {isCheapest && <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            delay: 0.4,
            type: 'spring',
            stiffness: 300
          }} className="absolute -top-2.5 -right-2 bg-emerald-500 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full shadow-md" data-id="element-13507" data-mp="1g7vkfbrzky6y">
                  ✓ Goedkoopst
                </motion.div>}

              <div className="flex items-center justify-between mb-2" data-id="element-13508" data-mp="16a7bnhxjpq5s">
                <div className="flex items-center gap-2" data-id="element-13509" data-mp="1bbmwb8vt9unn">
                  <div className={`w-3 h-3 rounded-full ${colors.accent}`} data-id="element-13510" data-mp="2hqnw5pnyiwzi" />
                  <span className={`text-sm font-bold ${colors.text}`} data-id="element-13511" data-mp="2hsrwxyqk7fvk">
                    {item.name}
                  </span>
                </div>
                <span className="text-lg font-bold text-slate-900" data-id="element-13512" data-mp="b2oo98y2i805">
                  {formatEuro(item.totalPrice)}
                </span>
              </div>

              {/* Calculation breakdown */}
              <div className="bg-slate-50 rounded-lg px-3 py-2 flex items-center justify-between" data-id="element-13513" data-mp="1bu2s6snbcfr">
                <span className="text-xs text-slate-500" data-id="element-13514" data-mp="3shvemhkfqa2g">
                  {formatEuro(item.totalPrice)} ÷ {formatNumber(item.quantity)}{' '}
                  {item.unit}
                </span>
                <span className={`text-sm font-bold ${isCheapest ? 'text-emerald-700' : 'text-slate-700'}`} data-id="element-13515" data-mp="3jhytur161vco">
                  = {formatEuro(item.unitPrice)} {compareUnit}
                </span>
              </div>
            </motion.div>;
      })}
      </div>

      {/* Educational explanation */}
      <div className="bg-white/80 rounded-xl p-4 border border-blue-100 shadow-sm" data-id="element-13516" data-mp="1cpoc84wyjlmi">
        <div className="flex items-start gap-3" data-id="element-13517" data-mp="17t7cedgebitl">
          <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-13518" data-mp="gnlg4l2as6qb">
            <span className="text-emerald-600 text-sm" data-id="element-13519" data-mp="lmodrkfy7ao9">💡</span>
          </div>
          <div data-id="element-13520" data-mp="7v9tlaab13fm">
            <h4 className="text-sm font-bold text-emerald-900 mb-1" data-id="element-13521" data-mp="7vjisvniz6k9">
              Hoe vergelijk je prijzen?
            </h4>
            <p className="text-sm text-slate-700 leading-relaxed" data-id="element-13522" data-mp="bvahdljamqzc">
              Bereken de <strong data-id="element-13523" data-mp="3u1ylb82lx83f">prijs {compareUnit}</strong> door de
              totaalprijs te delen door het aantal. De laagste prijs{' '}
              {compareUnit} is de beste deal.
              {savings !== null && savings > 0.001 && <span className="block mt-1 text-emerald-700 font-semibold" data-id="element-13524" data-mp="3l3cwwwageyuq">
                  Verschil: {formatEuro(savings)} {compareUnit} — dat scheelt{' '}
                  {formatEuro(savings * Math.max(...withUnitPrice.map(i => i.quantity)))}{' '}
                  bij de grootste verpakking!
                </span>}
            </p>
          </div>
        </div>
      </div>
    </div>;
}